import React from "react";
import MainLayout from "@/components/layout/MainLayout";
import AdminLayout from "@/components/settings/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { StripeSync } from "@/components/admin/StripeSync";

interface Organization {
  id: string;
  name: string;
  description?: string | null;
  is_super_admin?: boolean | null;
  // Include other fields as needed
}

const AdminDashboardPage = () => {
  const { data: organizations, isLoading } = useQuery({
    queryKey: ["admin", "organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .order("created_at", { ascending: false });

      if (error) {
        throw error;
      }

      return data as Organization[];
    },
  });

  return (
    <MainLayout>
      <AdminLayout
        title="Admin Dashboard"
        description="Manage system-wide settings and view organization data"
      >
        <Tabs defaultValue="organizations">
          <TabsList className="mb-6">
            <TabsTrigger value="organizations">Organizations</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="system">System Settings</TabsTrigger>
            <TabsTrigger value="stripe">Stripe Sync</TabsTrigger>
          </TabsList>

          <TabsContent value="organizations">
            <Card>
              <CardHeader>
                <CardTitle>Organizations</CardTitle>
                <CardDescription>
                  View and manage all organizations in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div>
                  </div>
                ) : organizations && organizations.length > 0 ? (
                  <div className="space-y-4">
                    {organizations.map((org) => (
                      <Card key={org.id} className="overflow-hidden">
                        <div className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-medium">
                                {org.name}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {org.id}
                              </p>
                            </div>
                            {org.is_super_admin && (
                              <span className="bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium">
                                Super Admin
                              </span>
                            )}
                          </div>
                          {org.description && (
                            <p className="mt-2 text-sm">{org.description}</p>
                          )}
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <p className="text-center py-8 text-muted-foreground">
                    No organizations found
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Users</CardTitle>
                <CardDescription>
                  View and manage users in the system
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-center py-8 text-muted-foreground">
                  User management coming soon
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="system">
            <Card>
              <CardHeader>
                <CardTitle>System Settings</CardTitle>
                <CardDescription>
                  Configure system-wide settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-center py-8 text-muted-foreground">
                  System settings coming soon
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="stripe">
            <StripeSync />
          </TabsContent>
        </Tabs>
      </AdminLayout>
    </MainLayout>
  );
};

export default AdminDashboardPage;
